import { useState } from "react";
import { t } from "../../../lib/i18n";
import { Screen, StudyGate } from "../kit";
import { Burst, Feedback, GameHeader, Intro, Result, YearChips, options, rnd, useYear } from "./common";

const ROUND = 10;

/** Shared flow for "look at the picture, tap one of four answers" maths games. */
function useRound<Q>(make: (year: number) => Q) {
  const [year, setYear] = useYear();
  const [q, setQ] = useState<Q | null>(null);
  const [n, setN] = useState(0);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [picked, setPicked] = useState<string | null>(null);
  const [burst, setBurst] = useState(0);
  const [done, setDone] = useState(false);
  const start = () => { setQ(make(year)); setN(0); setScore(0); setLives(3); setPicked(null); setDone(false); };
  const answer = (choice: string, right: string) => {
    if (picked !== null) return;
    setPicked(choice);
    const ok = choice === right;
    if (ok) { setScore((s) => s + 1); setBurst((b) => b + 1); } else setLives((l) => l - 1);
    setTimeout(() => {
      const left = ok ? lives : lives - 1;
      if (n + 1 >= ROUND || left <= 0) { setDone(true); return; }
      setN((i) => i + 1); setPicked(null); setQ(make(year));
    }, ok ? 800 : 1700);
  };
  return { year, setYear, q, n, score, lives, picked, burst, done, start, answer };
}

function Choices({ opts, right, picked, onPick }: { opts: string[]; right: string; picked: string | null; onPick: (o: string) => void }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {opts.map((o) => {
        const state = picked === null ? "" : o === right ? "bg-emerald-500 text-white ring-emerald-500" : o === picked ? "animate-shake bg-rose-500 text-white ring-rose-500" : "opacity-50";
        return (
          <button key={o} type="button" disabled={picked !== null} onClick={() => onPick(o)}
            className={`min-h-[4rem] rounded-2xl bg-card px-3 py-3 text-2xl font-black text-slate-900 shadow-sm ring-2 ring-black/5 transition ${state}`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

/* ── Jam Ajaib: read the analogue clock ──────────────────────────────────── */
type ClockQ = { h: number; m: number; pm: boolean; right: string; opts: string[]; ask24: boolean };
const pad = (n: number) => String(n).padStart(2, "0");
const h12 = (h: number, m: number) => `${h}:${pad(m)}`;
const h24 = (h: number, m: number, pm: boolean) => `${pad((h % 12) + (pm ? 12 : 0))}${pad(m)}`;

function makeClock(year: number): ClockQ {
  const h = rnd(1, 12);
  const m = year <= 1 ? 0 : year === 2 ? [0, 30][rnd(0, 1)]! : year === 3 ? [0, 15, 30, 45][rnd(0, 3)]! : year === 4 ? rnd(0, 11) * 5 : rnd(0, 59);
  const pm = Math.random() < 0.5;
  const ask24 = year >= 5;
  const fmt = (hh: number, mm: number, p: boolean) => (ask24 ? h24(hh, mm, p) : h12(hh, mm));
  const right = fmt(h, m, pm);
  const opts = options(right, () => {
    const r = Math.random();
    if (ask24 && r < 0.35) return fmt(h, m, !pm); // AM/PM trap
    if (r < 0.6) return fmt(m === 0 ? h : (h % 12) + 1, m, pm); // hour hand read as next hour
    const mm = year <= 2 ? [0, 30][rnd(0, 1)]! : year === 3 ? [0, 15, 30, 45][rnd(0, 3)]! : (Math.round(m / 5) * 5 + 5 * rnd(-3, 3) + 60) % 60;
    return fmt(rnd(1, 12), mm, pm);
  });
  return { h, m, pm, right, opts, ask24 };
}

export function ClockFace({ h, m, size = 220 }: { h: number; m: number; size?: number }) {
  const ha = ((h % 12) + m / 60) * 30, ma = m * 6;
  return (
    <svg viewBox="0 0 200 200" width={size} height={size} className="mx-auto drop-shadow-lg" role="img" aria-label={t("Jam analog")}>
      <circle cx="100" cy="100" r="96" fill="#FDE68A" />
      <circle cx="100" cy="100" r="86" fill="#fff" stroke="#F59E0B" strokeWidth="4" />
      {Array.from({ length: 60 }, (_, k) => {
        const a = (k * 6 * Math.PI) / 180, long = k % 5 === 0;
        return <line key={k} x1={100 + Math.sin(a) * (long ? 70 : 76)} y1={100 - Math.cos(a) * (long ? 70 : 76)} x2={100 + Math.sin(a) * 82} y2={100 - Math.cos(a) * 82} stroke="#334155" strokeWidth={long ? 3 : 1} strokeLinecap="round" />;
      })}
      {Array.from({ length: 12 }, (_, k) => {
        const a = ((k + 1) * 30 * Math.PI) / 180;
        return <text key={k} x={100 + Math.sin(a) * 58} y={100 - Math.cos(a) * 58 + 7} textAnchor="middle" fontSize="18" fontWeight="800" fill="#1E293B">{k + 1}</text>;
      })}
      <line x1="100" y1="100" x2={100 + Math.sin((ha * Math.PI) / 180) * 42} y2={100 - Math.cos((ha * Math.PI) / 180) * 42} stroke="#1E3A8A" strokeWidth="8" strokeLinecap="round" />
      <line x1="100" y1="100" x2={100 + Math.sin((ma * Math.PI) / 180) * 66} y2={100 - Math.cos((ma * Math.PI) / 180) * 66} stroke="#E11D48" strokeWidth="5" strokeLinecap="round" />
      <circle cx="100" cy="100" r="7" fill="#1E293B" />
    </svg>
  );
}

export function JamPage() { return <Screen><StudyGate><Jam /></StudyGate></Screen>; }
function Jam() {
  const g = useRound(makeClock);
  const header = <GameHeader title={t("Jam Ajaib")} emoji="🕒" tone="from-amber-400 to-orange-500" score={g.q ? g.score : undefined} lives={g.q ? g.lives : undefined} progress={g.q ? g.n / ROUND : undefined} />;
  if (!g.q) return <>{header}<Intro emoji="🕒" title={t("Jam Ajaib")} text={t("Baca jarum jam dan pilih waktu yang betul.")} onStart={g.start}><YearChips year={g.year} onYear={g.setYear} /></Intro></>;
  if (g.done) return <>{header}<Result game="jam" score={g.score} max={ROUND} onAgain={g.start} details={{ year: g.year }} /></>;
  const q = g.q;
  return (
    <>
      {header}
      <Burst show={g.burst} emoji={["⏰", "⭐", "✨"]} />
      <div key={g.n} className="animate-rise space-y-4">
        <ClockFace h={q.h} m={q.m} />
        <p className="text-center text-lg font-extrabold text-slate-800">
          {q.ask24 ? <>{q.pm ? "🌙 " : "☀️ "}{q.pm ? t("Waktu petang/malam. Apakah waktu dalam sistem 24 jam?") : t("Waktu pagi. Apakah waktu dalam sistem 24 jam?")}</> : t("Pukul berapa?")}
        </p>
        <Choices opts={q.opts} right={q.right} picked={g.picked} onPick={(o) => g.answer(o, q.right)} />
        <Feedback state={g.picked === null ? null : g.picked === q.right ? "ok" : "no"} right={q.right} />
      </div>
    </>
  );
}

/* ── Pizza Pecahan: shaded slices → fraction ─────────────────────────────── */
type PizzaQ = { parts: number; shaded: number; right: string; opts: string[]; simplest: boolean };
const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);

function makePizza(year: number): PizzaQ {
  const pool = year <= 1 ? [2, 4] : year === 2 ? [2, 3, 4] : year === 3 ? [2, 3, 4, 5, 6, 8] : [4, 6, 8, 10, 12];
  const parts = pool[rnd(0, pool.length - 1)]!;
  const simplest = year >= 4;
  let shaded = rnd(1, parts - 1);
  if (simplest) for (let guard = 0; gcd(shaded, parts) === 1 && guard < 20; guard++) shaded = rnd(1, parts - 1); // reducible ones
  const g = gcd(shaded, parts);
  const right = simplest ? `${shaded / g}/${parts / g}` : `${shaded}/${parts}`;
  const opts = options(right, () => {
    const r = Math.random();
    if (r < 0.3) return `${parts - shaded}/${parts}`; // counted the plain slices
    if (r < 0.5) return `${shaded}/${parts}` === right ? `${shaded}/${parts + 1}` : `${shaded}/${parts}`;
    const d = rnd(2, 8);
    return `${rnd(1, d - 1)}/${d}`;
  });
  return { parts, shaded, right, opts, simplest };
}

export function Pizza({ parts, shaded, size = 230 }: { parts: number; shaded: number; size?: number }) {
  const r = 88;
  const slice = (k: number) => {
    const a0 = (2 * Math.PI * k) / parts - Math.PI / 2, a1 = (2 * Math.PI * (k + 1)) / parts - Math.PI / 2;
    return `M100 100 L${100 + r * Math.cos(a0)} ${100 + r * Math.sin(a0)} A${r} ${r} 0 0 1 ${100 + r * Math.cos(a1)} ${100 + r * Math.sin(a1)}Z`;
  };
  const mid = (k: number, d: number) => { const a = (2 * Math.PI * (k + 0.5)) / parts - Math.PI / 2; return { x: 100 + d * Math.cos(a), y: 100 + d * Math.sin(a) }; };
  return (
    <svg viewBox="0 0 200 200" width={size} height={size} className="mx-auto drop-shadow-lg" role="img" aria-label={t("Pizza dengan {k} daripada {n} potong berlorek", { k: shaded, n: parts })}>
      <circle cx="100" cy="100" r="97" fill="#D97706" />
      {Array.from({ length: parts }, (_, k) => (
        <g key={k}>
          <path d={slice(k)} fill={k < shaded ? "#F87171" : "#FDE68A"} stroke="#B45309" strokeWidth="2.5" />
          {k < shaded && [0.45, 0.72].map((d, j) => { const p = mid(k + (j ? 0.15 : -0.15), r * d); return <circle key={j} cx={p.x} cy={p.y} r={parts > 8 ? 5 : 7} fill="#B91C1C" />; })}
          {k >= shaded && <circle cx={mid(k, r * 0.6).x} cy={mid(k, r * 0.6).y} r="3" fill="#FBBF24" />}
        </g>
      ))}
    </svg>
  );
}

export function PecahanPage() { return <Screen><StudyGate><Pecahan /></StudyGate></Screen>; }
function Pecahan() {
  const g = useRound(makePizza);
  const header = <GameHeader title={t("Pizza Pecahan")} emoji="🍕" tone="from-rose-400 to-orange-400" score={g.q ? g.score : undefined} lives={g.q ? g.lives : undefined} progress={g.q ? g.n / ROUND : undefined} />;
  if (!g.q) return <>{header}<Intro emoji="🍕" title={t("Pizza Pecahan")} text={t("Berapa bahagian pizza yang ada pepperoni? Pilih pecahan yang betul.")} onStart={g.start}><YearChips year={g.year} onYear={g.setYear} /></Intro></>;
  if (g.done) return <>{header}<Result game="pecahan" score={g.score} max={ROUND} onAgain={g.start} details={{ year: g.year }} /></>;
  const q = g.q;
  return (
    <>
      {header}
      <Burst show={g.burst} emoji={["🍕", "⭐", "✨"]} />
      <div key={g.n} className="animate-rise space-y-4">
        <Pizza parts={q.parts} shaded={q.shaded} />
        <p className="text-center text-lg font-extrabold text-slate-800">
          {q.simplest ? t("Berapa pecahan berlorek? (bentuk termudah)") : t("Berapa pecahan pizza yang berlorek merah?")}
        </p>
        <Choices opts={q.opts} right={q.right} picked={g.picked} onPick={(o) => g.answer(o, q.right)} />
        <Feedback state={g.picked === null ? null : g.picked === q.right ? "ok" : "no"} right={q.simplest ? `${q.shaded}/${q.parts} = ${q.right}` : q.right} />
      </div>
    </>
  );
}

/* ── Kedai Wang: pay the exact price with coins and notes ────────────────── */
type Money = { sen: number; kind: "coin" | "note" };
const ALL: Money[] = [
  { sen: 5, kind: "coin" }, { sen: 10, kind: "coin" }, { sen: 20, kind: "coin" }, { sen: 50, kind: "coin" },
  { sen: 100, kind: "note" }, { sen: 500, kind: "note" }, { sen: 1000, kind: "note" }, { sen: 2000, kind: "note" }, { sen: 5000, kind: "note" },
];
const ITEMS = ["🍎", "🍌", "🥤", "🍞", "✏️", "📒", "🧸", "⚽", "🎒", "🧃", "🍦", "🥚"];
const label = (sen: number) => (sen >= 100 ? `RM${(sen / 100).toFixed(sen % 100 ? 2 : 0)}` : `${sen} ${t("sen")}`);
const NOTE_COLORS: Record<number, string> = { 100: "#2563EB", 500: "#16A34A", 1000: "#DC2626", 2000: "#EA580C", 5000: "#0D9488" };

function priceFor(year: number): { price: number; money: Money[] } {
  if (year <= 2) return { price: rnd(1, 20) * 5, money: ALL.filter((m) => m.sen <= 100) };
  if (year <= 4) return { price: rnd(4, 200) * 5, money: ALL.filter((m) => m.sen <= 1000) };
  return { price: rnd(20, 1800) * 5, money: ALL };
}

function Piece({ m, onTap, small }: { m: Money; onTap?: () => void; small?: boolean }) {
  const s = small ? 0.7 : 1;
  return (
    <button type="button" onClick={onTap} aria-label={label(m.sen)} className="transition active:scale-95">
      {m.kind === "coin" ? (
        <svg width={56 * s} height={56 * s} viewBox="0 0 56 56">
          <circle cx="28" cy="28" r="26" fill={m.sen === 50 ? "#FCD34D" : "#CBD5E1"} stroke="#64748B" strokeWidth="2" />
          <circle cx="28" cy="28" r="20" fill="none" stroke="#64748B" strokeWidth="1" strokeDasharray="2 2" />
          <text x="28" y="33" textAnchor="middle" fontSize="15" fontWeight="800" fill="#1E293B">{m.sen}</text>
        </svg>
      ) : (
        <svg width={92 * s} height={52 * s} viewBox="0 0 92 52">
          <rect x="1" y="1" width="90" height="50" rx="6" fill={NOTE_COLORS[m.sen]} />
          <rect x="6" y="6" width="80" height="40" rx="4" fill="none" stroke="#fff" strokeOpacity=".6" />
          <circle cx="70" cy="26" r="11" fill="#fff" fillOpacity=".3" />
          <text x="30" y="33" textAnchor="middle" fontSize="17" fontWeight="900" fill="#fff">RM{m.sen / 100}</text>
        </svg>
      )}
    </button>
  );
}

export function WangPage() { return <Screen><StudyGate><Wang /></StudyGate></Screen>; }
function Wang() {
  const [year, setYear] = useYear();
  const WROUND = 8;
  const [round, setRound] = useState<{ price: number; money: Money[]; item: string } | null>(null);
  const [n, setN] = useState(0);
  const [score, setScore] = useState(0);
  const [tray, setTray] = useState<Money[]>([]);
  const [state, setState] = useState<"ok" | "no" | null>(null);
  const [burst, setBurst] = useState(0);
  const [done, setDone] = useState(false);
  const newRound = () => { setRound({ ...priceFor(year), item: ITEMS[rnd(0, ITEMS.length - 1)]! }); setTray([]); setState(null); };
  const start = () => { setN(0); setScore(0); setDone(false); newRound(); };
  const paid = tray.reduce((a, m) => a + m.sen, 0);
  function pay() {
    if (!round) return;
    const ok = paid === round.price;
    setState(ok ? "ok" : "no");
    if (ok) { setScore((s) => s + 1); setBurst((b) => b + 1); }
    setTimeout(() => { if (n + 1 >= WROUND) setDone(true); else { setN((i) => i + 1); newRound(); } }, ok ? 900 : 1800);
  }
  const header = <GameHeader title={t("Kedai Wang")} emoji="🪙" tone="from-emerald-400 to-teal-500" score={round ? score : undefined} progress={round ? n / WROUND : undefined} />;
  if (!round) return <>{header}<Intro emoji="🛒" title={t("Kedai Wang")} text={t("Bayar harga barang dengan tepat. Tekan syiling dan wang kertas, kemudian tekan Bayar.")} onStart={start}><YearChips year={year} onYear={setYear} /></Intro></>;
  if (done) return <>{header}<Result game="wang" score={score} max={WROUND} onAgain={start} details={{ year }} /></>;
  return (
    <>
      {header}
      <Burst show={burst} emoji={["🪙", "💵", "⭐"]} />
      <div key={n} className="animate-rise space-y-4">
        <div className="rounded-3xl bg-card p-4 text-center shadow-sm ring-1 ring-black/5">
          <p className="animate-bob text-6xl" aria-hidden>{round.item}</p>
          <p className="mt-1 text-sm font-bold text-slate-500">{t("Harga")}</p>
          <p className="text-4xl font-black text-slate-900">{label(round.price)}</p>
        </div>
        <div className={`min-h-[5.5rem] rounded-3xl border-2 border-dashed p-3 ${state === "ok" ? "border-emerald-400 bg-emerald-50" : state === "no" ? "animate-shake border-rose-400 bg-rose-50" : "border-slate-300 bg-white/60"}`}>
          <div className="mb-1 flex items-center justify-between text-sm font-bold text-slate-600">
            <span>{t("Duit anda")}</span>
            <span className={paid > round.price ? "text-rose-600" : "text-slate-900"}>{label(paid)}</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {tray.length === 0 && <p className="text-sm text-slate-400">{t("Tekan duit di bawah untuk bayar.")}</p>}
            {tray.map((m, i) => <Piece key={i} m={m} small onTap={() => state === null && setTray((tr) => tr.filter((_, k) => k !== i))} />)}
          </div>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          {round.money.map((m) => <Piece key={m.sen} m={m} onTap={() => state === null && setTray((tr) => [...tr, m])} />)}
        </div>
        <button type="button" disabled={state !== null || paid === 0} onClick={pay} className="w-full rounded-full bg-emerald-500 py-3 text-base font-extrabold text-white shadow-md disabled:opacity-40">
          {t("Bayar")} {label(paid)}
        </button>
        {state === "no" && <p className="text-center text-sm font-bold text-rose-600">{paid > round.price ? t("Terlebih {x}. Cuba kira semula.", { x: label(paid - round.price) }) : t("Kurang {x}.", { x: label(round.price - paid) })}</p>}
        {state === "ok" && <p className="text-center text-lg font-extrabold text-emerald-600">{t("Betul!")}</p>}
      </div>
    </>
  );
}
